import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getGroupIdFromUrl,
  getLiteReturnPath,
} from '../../utils/getGroupIdFromUrl';
import {
  getActivityResult,
  updateDeliveryStatus,
  type ActivityResult,
  getPrepaidInfo,
  upsertPrepaidInfo,
} from '../../api/liteActivitiesClient';
import { getLiteJwtToken } from '../../api/liteAuthToken';
import toast from 'react-hot-toast';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ChevronRight, MessageCircle } from 'lucide-react';
import { formatMoney } from '@pos/shared/money';
import { isLitePrepaidShopType } from '@pos/shared';

/** 由 productKey 解析出顯示名稱與單價（支援 productId:specId） */
function resolveProductKey(
  productList: Array<{
    id: string;
    name: string;
    price?: number;
    maxTotalQuantity?: number;
    specs?: Array<{ id: string; name: string; price: number }>;
  }>,
  productKey: string
): { name: string; price: number } {
  if (!productList?.length) return { name: productKey, price: 0 };
  const idx = productKey.indexOf(':');
  if (idx > 0) {
    const productId = productKey.slice(0, idx);
    const specId = productKey.slice(idx + 1);
    const product = productList.find(p => p.id === productId);
    if (product && 'specs' in product && product.specs?.length) {
      const spec = product.specs.find(s => s.id === specId);
      if (spec) return { name: spec.name, price: spec.price };
    }
  }
  const product = productList.find(p => p.id === productKey);
  if (product && typeof (product as { price?: number }).price === 'number')
    return {
      name: product.name,
      price: (product as { price: number }).price,
    };
  return { name: productKey, price: 0 };
}

/**
 * 活動結果頁面
 * 顯示活動結束後的結果；商品總覽與參與者詳情皆以 Drawer 開啟，交付/棄單僅作用於人（整筆）
 */
export function ActivityResultPage() {
  const navigate = useNavigate();
  const { activityId, suffix } = useParams<{
    activityId: string;
    suffix?: string;
  }>();
  const groupId = getGroupIdFromUrl();
  const isSquareMode = Boolean(
    suffix && suffix.length >= 6 && suffix.length <= 8
  );
  const squareContext = useMemo(
    () => (isSquareMode && suffix ? { squareSuffix: suffix } : undefined),
    [isSquareMode, suffix]
  );

  const [result, setResult] = useState<ActivityResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [participantDrawerUserId, setParticipantDrawerUserId] = useState<
    string | null
  >(null);
  const [prepaidDialogOpen, setPrepaidDialogOpen] = useState(false);
  const [prepaidLoading, setPrepaidLoading] = useState(false);
  const [prepaidTargetUserId, setPrepaidTargetUserId] = useState<string | null>(
    null
  );
  const [prepaidRemaining, setPrepaidRemaining] = useState(0);
  /** 欲增加的次數（字串以支援清空；送出時轉為 number，空字串視為 0） */
  const [prepaidAddInput, setPrepaidAddInput] = useState('');
  const [prepaidGender, setPrepaidGender] = useState<
    'male' | 'female' | 'other' | null
  >(null);
  const [prepaidNickname, setPrepaidNickname] = useState<string>('');
  /** 參與者 Drawer 開啟時顯示的預繳剩餘次數（開 Drawer 時載入） */
  const [prepaidDrawerRemaining, setPrepaidDrawerRemaining] = useState<
    number | null
  >(null);

  // 載入活動結果
  useEffect(() => {
    const loadResult = async () => {
      if (!activityId) {
        toast.error('缺少活動 ID');
        setIsLoading(false);
        return;
      }

      try {
        const jwtToken = getLiteJwtToken();
        if (!jwtToken) {
          toast.error('請先登入');
          setIsLoading(false);
          return;
        }

        const data = await getActivityResult(
          activityId,
          jwtToken,
          squareContext
        );
        setResult(data);
      } catch (error: unknown) {
        console.error('❌ [ActivityResultPage] 載入活動結果失敗:', error);
        toast.error(
          error instanceof Error ? error.message : '載入活動結果失敗'
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadResult();
  }, [activityId, isSquareMode, suffix, squareContext]);

  /** 參與者 Drawer 開啟時載入該員預繳剩餘次數（僅席次(羽球)類型） */
  useEffect(() => {
    if (!participantDrawerUserId || !activityId || !result) {
      setPrepaidDrawerRemaining(null);
      return;
    }
    if (!isLitePrepaidShopType(result.shopType)) {
      setPrepaidDrawerRemaining(null);
      return;
    }
    const jwt = getLiteJwtToken();
    if (!jwt) {
      setPrepaidDrawerRemaining(null);
      return;
    }
    let cancelled = false;
    getPrepaidInfo(activityId, participantDrawerUserId, jwt, squareContext)
      .then(info => {
        if (!cancelled) setPrepaidDrawerRemaining(info.remainingCount ?? 0);
      })
      .catch(() => {
        if (!cancelled) setPrepaidDrawerRemaining(null);
      });
    return () => {
      cancelled = true;
    };
  }, [participantDrawerUserId, activityId, result, squareContext]);

  /** 參與者列表：維持參與順序，但已有狀態（交付或棄單）的排到下面；須在 early return 前呼叫以符合 Rules of Hooks */
  const sortedParticipants = useMemo(() => {
    if (!result) return [];
    const noStatus: ActivityResult['participants'] = [];
    const hasStatus: ActivityResult['participants'] = [];
    for (const p of result.participants) {
      const allD = p.items.every(i => i.delivered);
      const allC = p.items.every(i => i.cancelled);
      if (allD || allC) hasStatus.push(p);
      else noStatus.push(p);
    }
    return [...noStatus, ...hasStatus];
  }, [result]);

  /** 對整個人批量設定交付或棄單（交付、棄單作用於人，非商品細項） */
  const handleSetParticipantDelivery = async (
    participant: ActivityResult['participants'][number],
    delivered?: boolean,
    cancelled?: boolean
  ) => {
    if (!activityId || !result) return;
    const jwtToken = getLiteJwtToken();
    if (!jwtToken) {
      toast.error('請先登入');
      return;
    }

    setResult(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        participants: prev.participants.map(p => {
          if (p.userId !== participant.userId) return p;
          return {
            ...p,
            items: p.items.map(item => {
              const next = { ...item };
              if (delivered !== undefined) {
                next.delivered = delivered;
                if (delivered) next.cancelled = false;
              }
              if (cancelled !== undefined) {
                next.cancelled = cancelled;
                if (cancelled) next.delivered = false;
              }
              return next;
            }),
          };
        }),
      };
    });

    try {
      await Promise.all(
        participant.items.map(item =>
          updateDeliveryStatus(
            activityId,
            item.itemId,
            delivered,
            cancelled,
            jwtToken,
            squareContext
          )
        )
      );
    } catch (e: unknown) {
      try {
        const data = await getActivityResult(
          activityId,
          jwtToken,
          squareContext
        );
        setResult(data);
      } catch {
        // 回滾失敗時略過，外層已顯示錯誤
        void 0;
      }
      toast.error(e instanceof Error ? e.message : '更新狀態失敗');
    }
  };

  // 處理返回：Square 回 /lite/shop/:suffix，Group 回 /lite?groupId=...
  const handleBack = () => {
    navigate(
      getLiteReturnPath({
        suffix: isSquareMode ? suffix : undefined,
        groupId,
      })
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">載入失敗</h3>
          <p className="text-gray-600 mb-6">無法載入活動結果</p>
          <button
            onClick={handleBack}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all"
          >
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* 標題區域 */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900 mb-4 flex items-center"
          >
            <span className="mr-2">←</span>
            <span>返回</span>
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">活動結果</h2>
          <p className="text-sm text-gray-600">{result.activity.title}</p>
        </div>

        {/* 商品總覽：整張卡片可點擊開啟 Drawer，加大觸控區域 */}
        <button
          type="button"
          onClick={() => setProductDrawerOpen(true)}
          className="w-full bg-white rounded-2xl shadow-lg p-4 mb-6 flex items-center justify-between text-left hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[56px]"
        >
          <h3 className="text-xl font-bold text-gray-900">商品總覽</h3>
          <ChevronRight className="w-6 h-6 text-gray-400 shrink-0" />
        </button>

        {/* 參與者列表：收合拿掉，統一用 Drawer；交付、棄單作用於人 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            參與者列表（依參與順序，已交付/棄單排於下方）
          </h3>
          <div className="space-y-1">
            {sortedParticipants.map((participant, idx) => {
              const allDelivered = participant.items.every(i => i.delivered);
              const allCancelled = participant.items.every(i => i.cancelled);

              return (
                <div
                  key={participant.userId}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 last:border-0"
                >
                  {/* 整列可點擊開啟參與者詳情 Drawer，僅交付/棄單 checkbox 不觸發 */}
                  <button
                    type="button"
                    onClick={() =>
                      setParticipantDrawerUserId(participant.userId)
                    }
                    className="flex-1 flex items-center gap-2 py-3 px-2 -mx-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[48px] text-left"
                    aria-label={`展開 ${participant.userName || '參與者'} 詳情`}
                  >
                    <span className="text-sm text-gray-500 shrink-0">
                      #{idx + 1}
                    </span>
                    <h4 className="font-semibold text-gray-900 min-w-0 flex-1 flex items-center gap-1.5">
                      {participant.userName || '未命名'}
                      {participant.remarks?.trim() && (
                        <MessageCircle
                          className="h-4 w-4 shrink-0 text-red-500"
                          aria-label="有備註"
                        />
                      )}
                    </h4>
                    <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                  </button>
                  <div
                    className="flex items-center gap-3 shrink-0 py-3"
                    onClick={e => e.stopPropagation()}
                  >
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allDelivered}
                        onChange={() =>
                          handleSetParticipantDelivery(
                            participant,
                            !allDelivered,
                            undefined
                          )
                        }
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">交付</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allCancelled}
                        onChange={() =>
                          handleSetParticipantDelivery(
                            participant,
                            undefined,
                            !allCancelled
                          )
                        }
                        className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">棄單</span>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 商品總覽 Drawer */}
        <Drawer open={productDrawerOpen} onOpenChange={setProductDrawerOpen}>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>商品總覽</DrawerTitle>
              <DrawerDescription>單價、總數量與設定值</DrawerDescription>
            </DrawerHeader>
            <div className="px-4 pb-6 space-y-4">
              {result.activity.productList.map(
                (product: Record<string, unknown>) => {
                  const specs = product.specs as
                    | Array<{ id: string; name: string; price: number }>
                    | undefined;
                  const isVariant = Array.isArray(specs) && specs.length > 0;
                  const pid = String(product.id);
                  const totalQuantity = isVariant
                    ? Object.entries(result.productSummary).reduce(
                        (sum, [k, v]) =>
                          k === pid || k.startsWith(pid + ':')
                            ? sum + (v?.totalQuantity ?? 0)
                            : sum,
                        0
                      )
                    : (result.productSummary[pid]?.totalQuantity ?? 0);
                  const limit = isVariant
                    ? Number(product.maxTotalQuantity ?? 0)
                    : Number((product as { quantity?: number }).quantity ?? 0);
                  return (
                    <div
                      key={String(product.id)}
                      className="flex items-center justify-between rounded-xl border bg-white px-4 py-3"
                    >
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {String(product.name)}
                        </h4>
                        {isVariant ? (
                          <p className="text-sm text-gray-600">
                            多規格：
                            {specs.map(s => `${s.name} $${s.price}`).join('、')}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-600">
                            單價：${(product as { price?: number }).price ?? 0}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          總數量：{totalQuantity}
                        </p>
                        {limit > 0 && (
                          <p className="text-sm text-gray-500">
                            設定值：{limit}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </DrawerContent>
        </Drawer>

        {/* 參與者詳情 Drawer（僅明細，交付/棄單在列表行人上） */}
        <Drawer
          open={!!participantDrawerUserId}
          onOpenChange={o => !o && setParticipantDrawerUserId(null)}
        >
          <DrawerContent>
            <DrawerHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <DrawerTitle>
                    {result.participants.find(
                      p => p.userId === participantDrawerUserId
                    )?.userName || '參與者詳情'}
                  </DrawerTitle>
                </div>
                {participantDrawerUserId &&
                  result &&
                  isLitePrepaidShopType(result.shopType) && (
                    <div className="shrink-0 flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        目前尚餘：
                        {prepaidDrawerRemaining !== null
                          ? prepaidDrawerRemaining
                          : '—'}
                      </span>
                      <button
                        type="button"
                        className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                        onClick={async () => {
                          if (!activityId) return;
                          const jwt = getLiteJwtToken();
                          if (!jwt) {
                            toast.error('請先登入');
                            return;
                          }
                          try {
                            setPrepaidLoading(true);
                            setPrepaidTargetUserId(participantDrawerUserId);
                            const info = await getPrepaidInfo(
                              activityId,
                              participantDrawerUserId,
                              jwt,
                              squareContext
                            );
                            setPrepaidRemaining(info.remainingCount ?? 0);
                            setPrepaidAddInput('');
                            setPrepaidGender(
                              info.gender && info.gender !== 'other'
                                ? info.gender
                                : null
                            );
                            setPrepaidNickname(info.nickname ?? '');
                            setPrepaidDialogOpen(true);
                          } catch (e) {
                            toast.error(
                              e instanceof Error
                                ? e.message
                                : '取得預繳資訊失敗'
                            );
                          } finally {
                            setPrepaidLoading(false);
                          }
                        }}
                        disabled={prepaidLoading}
                      >
                        設定預繳
                      </button>
                    </div>
                  )}
              </div>
            </DrawerHeader>
            <div className="px-4 pb-6 space-y-3">
              {(() => {
                const p = result.participants.find(
                  x => x.userId === participantDrawerUserId
                );
                if (!p) return null;
                let tot = 0;
                return (
                  <>
                    {p.remarks?.trim() && (
                      <div className="rounded-xl border bg-amber-50/80 px-4 py-3">
                        <div className="text-sm font-medium text-amber-900 mb-1">
                          備註
                        </div>
                        <p className="text-sm text-amber-800 whitespace-pre-wrap">
                          {p.remarks.trim()}
                        </p>
                      </div>
                    )}
                    {p.items.map(item => {
                      const { name, price } = resolveProductKey(
                        result.activity.productList,
                        item.productKey
                      );
                      tot += item.quantity * price;
                      return (
                        <div
                          key={item.itemId}
                          className="flex items-center justify-between rounded-xl border bg-white px-4 py-3"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{name}</p>
                            <p className="text-sm text-gray-600">
                              數量：{item.quantity} × {formatMoney(price)}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {item.delivered && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                已交付
                              </span>
                            )}
                            {item.cancelled && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                                已棄單
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div className="pt-2 border-t flex justify-between font-bold text-gray-900">
                      <span>總計</span>
                      <span>{formatMoney(tot)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      {/* 預繳設定 Dialog（僅席次(羽球)類型使用） */}
      {result && isLitePrepaidShopType(result.shopType) && (
        <Dialog open={prepaidDialogOpen} onOpenChange={setPrepaidDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>預繳次數設定</DialogTitle>
              <DialogDescription>
                設定此成員的預繳次數、性別與顯示名稱。
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">目前預繳次數</span>
                <span className="text-lg font-semibold text-gray-900">
                  {prepaidRemaining}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  欲增加的次數
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={prepaidAddInput}
                  onChange={e => {
                    const raw = e.target.value;
                    if (raw === '' || /^\d+$/.test(raw)) {
                      setPrepaidAddInput(raw);
                    }
                  }}
                  placeholder="0"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  性別
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'male' as const, label: '男' },
                    { value: 'female' as const, label: '女' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPrepaidGender(opt.value)}
                      className={`flex-1 rounded-full border px-3 py-1 text-xs font-medium ${
                        prepaidGender === opt.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  暱稱
                </label>
                <input
                  type="text"
                  value={prepaidNickname}
                  onChange={e => setPrepaidNickname(e.target.value)}
                  placeholder="留空以 LINE 名稱顯示"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  maxLength={50}
                />
              </div>
            </div>
            <DialogFooter>
              <button
                type="button"
                className="inline-flex justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setPrepaidDialogOpen(false)}
                disabled={prepaidLoading}
              >
                取消
              </button>
              <button
                type="button"
                className="inline-flex justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={
                  prepaidLoading ||
                  !prepaidTargetUserId ||
                  ((prepaidAddInput === '' ||
                    parseInt(prepaidAddInput, 10) === 0) &&
                    !prepaidGender &&
                    !prepaidNickname)
                }
                onClick={async () => {
                  if (!activityId || !prepaidTargetUserId) return;
                  const jwt = getLiteJwtToken();
                  if (!jwt) {
                    toast.error('請先登入');
                    return;
                  }
                  const addCount =
                    prepaidAddInput === ''
                      ? 0
                      : Math.max(
                          0,
                          Math.floor(parseInt(prepaidAddInput, 10) || 0)
                        );
                  try {
                    setPrepaidLoading(true);
                    const payloadGender = prepaidGender;
                    const resultInfo = await upsertPrepaidInfo(
                      activityId,
                      prepaidTargetUserId,
                      {
                        addCount,
                        gender: payloadGender,
                        nickname: prepaidNickname || null,
                      },
                      jwt,
                      squareContext
                    );
                    setPrepaidRemaining(resultInfo.remainingCount ?? 0);
                    setPrepaidAddInput('');
                    setPrepaidGender(
                      resultInfo.gender && resultInfo.gender !== 'other'
                        ? resultInfo.gender
                        : null
                    );
                    setPrepaidNickname(resultInfo.nickname ?? '');
                    setPrepaidDrawerRemaining(resultInfo.remainingCount ?? 0);
                    toast.success('預繳設定已儲存');
                    setPrepaidDialogOpen(false);
                  } catch (e) {
                    toast.error(
                      e instanceof Error ? e.message : '預繳設定儲存失敗'
                    );
                  } finally {
                    setPrepaidLoading(false);
                  }
                }}
              >
                儲存
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
